/**
 * TypeSync Custom User ID Utilities
 * 
 * This module provides functions to create custom user IDs based on email prefixes
 * instead of using Firebase's default random UIDs.
 * 
 * For example:
 * - abc123@gmail.com -> abc123
 * - ved68312@gmail.com -> ved68312
 */

/**
 * Extract custom user ID from email address
 * @param {string} email - User's email address
 * @returns {string} - Custom user ID (email prefix)
 */
export const getCustomUserId = (email) => {
  if (!email || typeof email !== 'string') {
    throw new Error('Invalid email provided');
  }
  
  const emailPrefix = email.split('@')[0];
  
  if (!emailPrefix) {
    throw new Error('Invalid email format');
  }
  
  // Sanitize the prefix to ensure it's safe for Firebase paths
  // Remove any characters that aren't alphanumeric, underscore, or hyphen
  const sanitizedPrefix = emailPrefix.replace(/[^a-zA-Z0-9_-]/g, '');
  
  if (!sanitizedPrefix) {
    throw new Error('Email prefix contains no valid characters');
  }
  
  return sanitizedPrefix;
};

/**
 * Get custom user ID from Firebase user object
 * @param {Object} user - Firebase user object
 * @returns {string} - Custom user ID
 */
export const getCustomUserIdFromUser = (user) => {
  if (!user || !user.email) {
    throw new Error('User object or email not provided');
  }
  
  return getCustomUserId(user.email);
};

/**
 * Validate if a string is a valid custom user ID
 * @param {string} userId - User ID to validate
 * @returns {boolean} - Whether the user ID is valid
 */
export const isValidCustomUserId = (userId) => {
  if (!userId || typeof userId !== 'string') {
    return false;
  }
  
  // Check if it only contains alphanumeric characters, underscores, and hyphens
  return /^[a-zA-Z0-9_-]+$/.test(userId);
};

/**
 * Migration helper: Check if we need to migrate data from old UID to new custom ID
 * This is useful for existing users who already have data under Firebase's random UID
 * @param {string} firebaseUid - Original Firebase UID
 * @param {string} customUserId - New custom user ID
 * @returns {Object} - Migration info
 */
export const getMigrationInfo = (firebaseUid, customUserId) => {
  return {
    needsMigration: firebaseUid !== customUserId,
    oldPath: `users/${firebaseUid}`,
    newPath: `users/${customUserId}`,
    firebaseUid,
    customUserId
  };
};

/**
 * Legacy mapping for existing users (especially for ved68312@gmail.com)
 * This ensures backwards compatibility with existing data
 */
export const LEGACY_USER_MAPPINGS = {
  'ved68312@gmail.com': 'Jc4SGMqE6KbDVLNFXLmz6dc3VUt1', // Your existing Firebase UID
};

/**
 * Get user ID with legacy support
 * This function first checks if there's a legacy mapping, otherwise uses custom ID
 * @param {string} email - User's email
 * @returns {string} - User ID (legacy or custom)
 */
export const getUserIdWithLegacySupport = (email) => {
  if (LEGACY_USER_MAPPINGS[email]) {
    console.log(`Using legacy UID for ${email}: ${LEGACY_USER_MAPPINGS[email]}`);
    return LEGACY_USER_MAPPINGS[email];
  }
  
  return getCustomUserId(email);
};

/**
 * Get user ID from Firebase user object with legacy support
 * @param {Object} user - Firebase user object
 * @returns {string} - User ID (legacy or custom)
 */
export const getUserIdFromUserWithLegacySupport = (user) => {
  if (!user || !user.email) {
    throw new Error('User object or email not provided');
  }
  
  return getUserIdWithLegacySupport(user.email);
};
